import React, { useContext } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import { CloseIcon, MetadataGlyph24Icon, MoreIcon } from 'assets/icons'
import CustomMetadataListItemRow from 'src/apps/InitiativeSettings/components/CustomMetadataListItemRow'
import ModalRoute from 'src/components/utils/ModalRoute'
import Popover from 'src/components/utils/Popover'
import { useUpdateInitiative } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import ThemeContext from 'src/libraries/contexts/Theme'
import FieldUpsert from './FieldUpsert'

const CustomMetadataEditor = ({ onCloseButtonClick }) => {
   const match = useRouteMatch()
   const theme = useContext(ThemeContext)
   const { showNotification } = useContext(NotificationManagerContext)

   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()

   const handleRemoveField = async (key) => {
      try {
         await updateInitiative({ id: initiative.id }, {
            metadata: [{
               operation: 'remove',
               remove: key
            }]
         })

         showNotification({
            message: 'Metadata field was removed successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err?.graphQLErrors?.map(e => e.message).join(', ') || err.message,
            title: 'Error',
            type: 'error'
         })
      }
   }

   return (
      <View
         borderRadius="3px"
         style={{ overflow: 'hidden' }}
         float="left"
         width="100%"
         height="100%"
         scroll="on[a-b]">
         <View
            position="absolute"
            top="0px"
            right="0px"
            width="100%[a-b] calc(100% - 240px)[c-f]"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            float="left"
            height="60px"
            padding="0px 15px"
            borderBottom="1px solid #eee[c-f]">
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               onClick={onCloseButtonClick}>
               <CloseIcon width="24px" height="24px" />
            </View>
         </View>
         <View
            display="flex"
            float="left"
            width="100%[a-b] 240px[c-f]"
            height="100%[c-f]"
            borderBottom="1px solid #eee[a-b]"
            borderRight="1px solid #eee[c-f]"
            padding="20px[a-b] 25px[c-f]">
            <View>
               <View
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="35px"
                  height="35px"
                  borderRadius="50%"
                  backgroundColor="#000">
                  <MetadataGlyph24Icon
                     width="20px"
                     height="20px"
                     strokeWidth="2px"
                     color="#fff"
                  />
               </View>
               <Spacer size="2" />
               <Text
                  size="5"
                  color="#000"
                  weight="medium">
                  Custom Metadata
               </Text>
               <Spacer size="2" />
               <Text size="2" color="#777">
                  {`Custom Metadata is an advanced feature that lets you create data that can be used dynamically throughout for this ${initiative.type.nameSingular}.`}
               </Text>
               <Spacer size="5" />
               <Button
                  linkTo={`${match.url}/create`}
                  width="100%"
                  name="Add Field"
                  color={theme.protoSettingsHighlightBackgroundColor}
                  textColor={theme.protoSettingsHighlightForegroundColor}
                  textWeight="medium"
                  size="md"
                  textSize="0.9"
                  padding="25px"
               />
            </View>
         </View>
         <View
            float="left"
            width="100%[a-b] calc(100% - 240px)[c-f]"
            height="100%[c-f]">
            <View
               float="left"
               width="100%"
               height="calc(100% - 60px)"
               marginTop="60px[c-f]"
               scroll="on">
               {initiative.metadata.length === 0 && (
                  <View
                     float="left"
                     textAlign="center"
                     backgroundColor="#fff"
                     width="100%"
                     padding="30px">
                     <Text size="1.5" color="#666">
                        No metadata fields have been created yet
                     </Text>
                  </View>
               )}
               <Popover.Provider>
                  {initiative.metadata.map((field, index) => (
                     <CustomMetadataListItemRow
                        key={field.id}
                        keyName={field.key}
                        id={field.id}
                        description={field.description}
                        value={field.value}
                        action={(
                           <Popover.Anchor meta={field} tabIndex={index}>
                              <MoreIcon
                                 width="24px"
                                 height="24px"
                                 color="#aaa"
                              />
                           </Popover.Anchor>
                        )}
                     />
                  ))}
                  <Popover.View
                     anchorOriginHorizontal="right"
                     anchorOriginVertical="top"
                     backgroundColor="#fff"
                     borderRadius="5px"
                     width="150px">
                     {field => (
                        <View
                           float="left"
                           width="100%"
                           backgroundColor="#fff"
                           borderRadius="5px"
                           boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                           <ListMenu buttonSize="sm">
                              <ListMenuButton
                                 linkTo={`${match.url}/update/${field.key}`}
                                 name="Edit Field"
                              />
                              <ListMenuButton
                                 onClick={() => handleRemoveField(field.key)}
                                 name="Remove Field"
                              />
                           </ListMenu>
                        </View>
                     )}
                  </Popover.View>
               </Popover.Provider>
            </View>
         </View>
         <ModalRoute path={`${match.path}/:operationName(create|update)/:metadataKey?`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="5px"
                  width="100%[a-b] 270px[c-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}>
                  <FieldUpsert
                     onCloseButtonClick={onCloseTrigger}
                     onSave={onCloseTrigger}
                  />
               </Modal>
            )}
         </ModalRoute>
      </View>
   )
}

CustomMetadataEditor.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default CustomMetadataEditor
